import type { Moment } from 'moment';
import { DbContactsGroupedFromDb } from 'api/useNewDeal';

/** Minimum trimmed length for the "no document" reason in the client benchmark request. */
export const NO_DOCUMENT_TEXT_MIN_LENGTH = 30;

/**
 * Form values for the Create New Deal form.
 * Used for both create and edit modes.
 */
export interface DealContactFormValue {
  contactType?: string;
  contact?: string;
}

/** Supplier/reseller item for multi-select picker. */
export interface SupplierResellerItem {
  id: string | number;
  name: string;
  isPrimary: boolean;
  /**
   * True when this supplier is NOT registered in Vantage and was captured as free text. `id` is then
   * a client-side-only key, so the submit mapping must send `vendor_name` — never `vendor_id`.
   */
  isUnregistered?: boolean;
  /**
   * `deal_party.id` this row was hydrated from (edit mode only). Retained when an analyst resolves a
   * pending supplier so the submit mapping can name the exact party to link — inferring it from the
   * before/after supplier lists would be guesswork.
   */
  partyId?: string;
  /**
   * True when this row was hydrated as a pending (free-text) supplier. Kept after it is resolved so
   * the submit mapping can tell "resolved an existing party" (→ `vendor_links`) from "added a brand
   * new supplier" (→ `vendors`) — `isUnregistered` alone is cleared by the resolve and cannot.
   */
  wasPending?: boolean;
}
export interface ContactTypeOption {
  value: string;
  label: React.ReactNode;
  type: 'client' | 'npi';
  /** Max assignees for this type; `null` means unlimited. */
  maxSlots: number | null;
  visualLabel: string;
}
export interface CreateNewDealFormValues {
  /** Client table id from GET /clients/search */
  client: number;
  suppliers: SupplierResellerItem[];
  reseller?: SupplierResellerItem[];
  dealTitle?: string;
  dealType?: string;
  /** lov_report_type id from GET /lov/report-type */
  reportTypeRequested?: number;
  /** Persisted TCV tier id (`lov_tcv_tier.id`) for portfolio intake (VE-2236). */
  tcvTierId?: number;
  budgetedCost: number;
  /** Ant Design DatePicker value (moment in this codebase) */
  targetDate: Moment | null | undefined;
  contractStartDate?: Moment | null;
  contractEndDate?: Moment | null;
  /**
   * Whether the deal renders on the Smart Calendar. Shown beside the title on create and edit;
   * defaults to true (the column default). Sent as `show_on_smart_calendar`.
   */
  showOnSmartCalendar?: boolean;
  /** Notice period before contract termination, in whole days (0–127). Optional. Sent as `notice_period_days_required`. */
  notice_period_days_required?: number;
  dataUseRights: string;
  clientSubscriptionPlan: string;
  contacts: DbContactsGroupedFromDb;
  situationSummary?: string;
  generalNotes: string;
  /** Client benchmark request only: whether the user attached a document or is explaining why not. */
  documentMode?: 'have' | 'none';
  /** Client benchmark request only: reason no document is attached (required, min 30 chars, when documentMode==='none'). */
  noDocumentText?: string;
  findingsRecommendationsOutcome?: string;
  savings_target: number;
  actual_savings: number;
  initial_quote_year_1?: number;
  initial_quote_full_contract_term?: number;
  npi_savings_target_year_1?: number;
  actual_savings_year_1?: number;
  /** ISO 4217 code; sent as `currency_code` on POST/PATCH /deals. */
  currency_code: string;
  /**
   * Categories from CategoryManager (matches benchmark page shape). Sent on POST/PATCH /deals as `categories[]`
   * — flattened to one row per `categorySecondIds` entry (or one row with no second when none selected).
   */
  category?: {
    mainCategory: string;
    subCategory: string;
    productCategories: string[];
    categoryRootId: number;
    categoryPrimeId: number;
    categorySecondIds: number[];
  }[];
  /**
   * Tag ids (number for existing tags, sentinel strings for newly typed tags before save). Sent on POST/PATCH /deals
   * as `tag_ids` (numeric) and `new_tags` (name/color for sentinel ids) when user tag creation is enabled.
   */
  tagIds?: (number | string)[];
}
export const DEAL_TYPE_OPTIONS = [
  { value: 'New_purchase', label: 'New Purchase' },
  { value: 'Renewal', label: 'Renewal' },
  { value: 'Add_on_purchase', label: 'Add-On' }
];
export const DATA_USE_RIGHTS_OPTIONS = [
  //add label then add a small dot with the color use a span and a css ::after for the dot no need for div
  {
    value: 'GREEN',
    label: (
      <div className="flex items-center gap-2">
        <span className="w-4 h-4 bg-green-500 rounded-full" /> Green
      </div>
    )
  },
  {
    value: 'YELLOW',
    label: (
      <div className="flex items-center gap-2">
        <span className="w-4 h-4 bg-yellow-500 rounded-full" /> Yellow
      </div>
    )
  },
  {
    value: 'RED',
    label: (
      <div className="flex items-center gap-2">
        <span className="w-4 h-4 bg-red-500 rounded-full" /> Red
      </div>
    )
  }
];
